import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MarketplaceHomeHeader from '../../components/marketplace/MarketplaceHomeHeader';
import MarketplaceHomeDesktop from '../../components/marketplace/MarketplaceHomeDesktop';
import Footer from '../../components/Footer';
import RequirementModal from '../../components/RequirementModal';
import BecomeSupplierModal from '../../components/BecomeSupplierModal';
import FindBuyerModal from '../../components/FindBuyerModal';
import ProductDetailsModal from '../../components/ProductDetailsModal';
import LoginModal from '../../components/LoginModal';
import RegisterModal from '../../components/RegisterModal';
import { useInquiry } from '../../context/InquiryContext';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import { productsApi, sellersApi, brandsApi, favoritesApi, categoriesApi } from '../../services/apiService';
import { LOGO_PLACEHOLDER } from '../../utils/placeholders';
import { SOCKET_URL as API_BASE } from '../../config/api';
import AdBanner from '../../components/AdBanner/AdBanner';
import MobileBottomNav from '../../components/MobileBottomNav/MobileBottomNav';
import './HomePage.css';
import '../../components/marketplace/marketplace-shell.css';

const HomePage = () => {
  // Navigation hook
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auth context for user info
  const { user, isAuthenticated } = useAuth();
  const { formatPriceRange, selectedCurrency } = useCurrency();
  
  // Login redirect state
  const [loginRedirectAction, setLoginRedirectAction] = useState(null);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);

  // Check if redirected from protected route - show login modal
  useEffect(() => {
    if (location.state?.showLogin) {
      setShowLoginModal(true);
      if (location.state?.from?.pathname) {
        const fullPath = location.state.from.pathname + (location.state.from.search || '');
        setLoginRedirectPath(fullPath);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.state?.mobileNavTab) return;
    setMobileNavTab(location.state.mobileNavTab);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Helper function to open requirement modal with login check
  const openRequirementModalWithAuth = () => {
    if (!isAuthenticated) {
      setLoginRedirectAction('requirement');
      setShowLoginModal(true);
      return;
    }
    setShowRequirementModal(true);
  };
  
  // Inquiry context for adding products to inquiry
  const { addToInquiry, isInInquiry, removeFromInquiry } = useInquiry();
  
  // Modal states
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [showBecomeSupplierModal, setShowBecomeSupplierModal] = useState(false);
  const [showFindBuyerModal, setShowFindBuyerModal] = useState(false);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  useEffect(() => {
    const openLogin = () => setShowLoginModal(true);
    const openReg = () => setShowRegisterModal(true);
    window.addEventListener('marketplace-open-login', openLogin);
    window.addEventListener('marketplace-open-register', openReg);
    return () => {
      window.removeEventListener('marketplace-open-login', openLogin);
      window.removeEventListener('marketplace-open-register', openReg);
    };
  }, []);

  // Mobile Alibaba-style tab states
  const [mobileNavTab, setMobileNavTab] = useState('Products');
  const [mobileCatTab, setMobileCatTab] = useState('All');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [mobileListening, setMobileListening] = useState(false);
  const mobileSearchRef = React.useRef(null);
  const mobileCameraInputRef = React.useRef(null);

  // Mobile camera: open file picker, use filename as query
  const handleMobileCameraClick = () => {
    mobileCameraInputRef.current?.click();
  };

  const handleMobileCameraFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ');
    setMobileSearchQuery(name);
    e.target.value = '';
  };

  // Mobile mic: speech-to-text
  const handleMobileMicClick = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice search is not supported in this browser.'); return; }
    const recognition = new SR();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    setMobileListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      if (transcript.trim()) setMobileSearchQuery(transcript.trim());
    };
    recognition.onerror = () => setMobileListening(false);
    recognition.onend = () => setMobileListening(false);
    recognition.start();
  };

  // Manufacturer tab states
  const [mfgCategories, setMfgCategories] = useState([]);
  const [mfgSelectedCat, setMfgSelectedCat] = useState('All');
  const [mfgCatModalOpen, setMfgCatModalOpen] = useState(false);
  const [mfgAllProducts, setMfgAllProducts] = useState([]);
  const [mfgAllSellers, setMfgAllSellers] = useState([]);
  const [mfgLoading, setMfgLoading] = useState(false);
  const [mfgDataLoaded, setMfgDataLoaded] = useState(false);

  // State for real data from API
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addedProducts, setAddedProducts] = useState({});
  const [favoriteIds, setFavoriteIds] = useState([]);

  // Fetch favorite IDs when user logs in
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (user?.id) {
        try {
          const response = await favoritesApi.getIds(user.id);
          setFavoriteIds(response.sellerIds || []);
        } catch (error) {
          console.error('Error fetching favorite IDs:', error);
        }
      }
    };
    fetchFavoriteIds();
  }, [user?.id]);

  // Toggle favorite seller
  const handleToggleFavorite = async (sellerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      alert('Please login to save favorites');
      return;
    }

    try {
      const response = await favoritesApi.toggle(user.id, sellerId);
      if (response.isFavorited) {
        setFavoriteIds(prev => [...prev, sellerId]);
      } else {
        setFavoriteIds(prev => prev.filter(id => id !== sellerId));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Handle inquire button click
  const handleInquireClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const seller = product.seller || {};
    const productData = {
      id: product.id,
      image: product.image || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500",
      description: product.description || product.name,
      priceMin: product.priceMin || 0,
      priceMax: product.priceMax || 0,
      priceUnit: selectedCurrency.code,
      pricePer: product.unit || 'Unit',
      moq: product.moq || '1 Unit',
      // Use seller.name as the company name (seller profile has the real company name)
      companyName: seller.name || 'Unknown Seller',
      // Use seller.userId for messaging (links seller to their user account)
      sellerId: seller.userId || product.userId,
      sellerName: seller.name || 'Unknown Seller',
      sellerAvatar: seller.logo || LOGO_PLACEHOLDER,
      country: seller.country || 'UAE',
      countryFlag: seller.countryFlag || 'https://flagcdn.com/w80/ae.png'
    };
    
    if (isInInquiry(product.id)) {
      removeFromInquiry(product.id);
      setAddedProducts(prev => ({ ...prev, [product.id]: false }));
    } else {
      addToInquiry(productData);
      setAddedProducts(prev => ({ ...prev, [product.id]: true }));
      // Show "Added!" feedback for 2 seconds
      setTimeout(() => {
        setAddedProducts(prev => ({ ...prev, [product.id]: 'done' }));
      }, 2000);
    }
  };

  // Fetch all data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [featuredRes, latestRes, popularRes, sellersRes, brandsRes] = await Promise.all([
          productsApi.getFeatured(),
          productsApi.getLatest(),
          productsApi.getPopular(),
          sellersApi.getTop(),
          brandsApi.getHighlighted() // Only show brands from sellers with canHighlightBrand permission
        ]);

        // Debug: Log to verify user data is coming through
        console.log('Featured products:', featuredRes);
        if (featuredRes.length > 0) {
          console.log('First product user:', featuredRes[0].user);
          console.log('First product userId:', featuredRes[0].userId);
        }

        // Safety filter: only show published items on frontend
        const filterPublished = (items) => items.filter(i => i.status === 'published');
        const filterPublishedSellers = (items) => items.filter(i => i.status === 'published');
        setFeaturedProducts(filterPublished(featuredRes).slice(0, 5));
        setNewProducts(filterPublished(latestRes).slice(0, 5));
        setPopularProducts(filterPublished(popularRes).slice(0, 5));
        setTopSellers(filterPublishedSellers(sellersRes).slice(0, 4));
        // Sort brands: newest brands first (by createdAt descending)
        const sortedBrands = [...brandsRes].sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        console.log('Brands data:', sortedBrands.slice(0, 7));
        setBrands(sortedBrands.slice(0, 7));
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch manufacturer tab data when tab is activated
  useEffect(() => {
    if (mobileNavTab !== 'Manufacturers' || mfgDataLoaded) return;
    const fetchMfgData = async () => {
      try {
        setMfgLoading(true);
        const [catsRes, productsRes, sellersRes] = await Promise.all([
          categoriesApi.getAll(),
          productsApi.getAll(),
          sellersApi.getAll()
        ]);
        const cats = Array.isArray(catsRes) ? catsRes : (catsRes.categories || []);
        setMfgCategories(cats);
        const prods = Array.isArray(productsRes) ? productsRes : (productsRes.products || []);
        setMfgAllProducts(prods.filter(p => p.status === 'published'));
        const sellers = Array.isArray(sellersRes) ? sellersRes : (sellersRes.sellers || []);
        setMfgAllSellers(sellers.filter(s => s.status === 'published'));
        setMfgDataLoaded(true);
      } catch (error) {
        console.error('Error fetching manufacturer data:', error);
      } finally {
        setMfgLoading(false);
      }
    };
    fetchMfgData();
  }, [mobileNavTab, mfgDataLoaded]);

  // Derived: group products by seller for manufacturer view
  const mfgFilteredProducts = mfgSelectedCat === 'All'
    ? mfgAllProducts
    : mfgAllProducts.filter(p => {
        const cat = p.category || {};
        return cat.name === mfgSelectedCat || String(cat.id) === String(mfgSelectedCat);
      });

  const mfgGroupedBySeller = React.useMemo(() => {
    const groups = {};
    mfgFilteredProducts.forEach(p => {
      const seller = p.seller || {};
      const key = seller.id || 'unknown';
      if (!groups[key]) {
        groups[key] = { seller, products: [] };
      }
      groups[key].products.push(p);
    });
    return Object.values(groups).filter(g => g.seller.id);
  }, [mfgFilteredProducts]);

  // Mobile search: filter products and manufacturers dynamically
  const mobileSearchLower = mobileSearchQuery.trim().toLowerCase();

  const matchesSearch = (text) => !mobileSearchLower || (text || '').toLowerCase().includes(mobileSearchLower);

  const mobileFilteredFeatured = featuredProducts.filter(p =>
    matchesSearch(p.name) || matchesSearch(p.description) || matchesSearch(p.category) || matchesSearch(p.seller?.name)
  );
  const mobileFilteredNew = newProducts.filter(p =>
    matchesSearch(p.name) || matchesSearch(p.description) || matchesSearch(p.category) || matchesSearch(p.seller?.name)
  );
  const mobileFilteredPopular = popularProducts.filter(p =>
    matchesSearch(p.name) || matchesSearch(p.description) || matchesSearch(p.category) || matchesSearch(p.seller?.name)
  );
  const mobileFilteredMfgProducts = mfgFilteredProducts.filter(p =>
    matchesSearch(p.name) || matchesSearch(p.description) || matchesSearch(p.category) || matchesSearch(p.seller?.name)
  );
  const mobileFilteredMfgSellers = mfgAllSellers.filter(s =>
    matchesSearch(s.name) || matchesSearch(s.businessType) || matchesSearch(s.country)
  );
  const mobileFilteredMfgGrouped = React.useMemo(() => {
    const groups = {};
    mobileFilteredMfgProducts.forEach(p => {
      const seller = p.seller || {};
      const key = seller.id || 'unknown';
      if (!groups[key]) groups[key] = { seller, products: [] };
      groups[key].products.push(p);
    });
    return Object.values(groups).filter(g => g.seller.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileFilteredMfgProducts]);

  const handleMobileSearch = () => {
    // no-op: search is now real-time
  };

  return (
    <div className="home-page">
      <MarketplaceHomeHeader />

      <main className="main-content">
        <MarketplaceHomeDesktop
          featuredProducts={featuredProducts}
          newProducts={newProducts}
          popularProducts={popularProducts}
          topSellers={topSellers}
          brands={brands}
          loading={loading}
          addedProducts={addedProducts}
          isInInquiry={isInInquiry}
          onInquire={handleInquireClick}
          favoriteIds={favoriteIds}
          onToggleFavorite={handleToggleFavorite}
          isAuthenticated={isAuthenticated}
          onSendRequirement={openRequirementModalWithAuth}
          onBecomeSupplier={() => setShowBecomeSupplierModal(true)}
          onFindBuyers={() => setShowFindBuyerModal(true)}
        />
      </main>

      {/* ===== FOOTER AD BANNER ===== */}
      <AdBanner placementSlug="footer-banner" className="ad-banner-fullwidth ad-banner-footer" />

      <Footer />

      {/* ===== MOBILE ALIBABA-STYLE HOME - HIDDEN ON DESKTOP ===== */}
      <div className="m-ali-home">
        {/* Mobile Top Nav Tabs */}
        <div className="m-ali-nav">
          {['AI Mode', 'Products', 'Manufacturers', 'Worldwide'].map(tab => (
            <button
              key={tab}
              className={`m-ali-nav-tab ${mobileNavTab === tab ? 'active' : ''}`}
              onClick={() => {
                if (tab === 'AI Mode') {
                  navigate('/mobile/ai-mode');
                  return;
                }
                if (tab === 'Worldwide') {
                  navigate('/mobile/worldwide');
                  return;
                }
                setMobileNavTab(tab);
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Mobile Search Bar */}
        <div className="m-ali-search">
          <div className="m-ali-search-bar">
            {/* Hidden file input for camera/image search */}
            <input
              ref={mobileCameraInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleMobileCameraFile}
            />
            <button
              type="button"
              onClick={handleMobileCameraClick}
              aria-label="Search by image"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <svg className="m-ali-search-camera" width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="5" width="20" height="15" rx="2" stroke="#666" strokeWidth="1.5" fill="none"/>
                <circle cx="12" cy="12.5" r="3.5" stroke="#666" strokeWidth="1.5" fill="none"/>
                <rect x="6" y="2" width="4" height="3" rx="1" stroke="#666" strokeWidth="1" fill="none"/>
              </svg>
            </button>
            <div className="m-ali-search-input" style={{ padding: 0, flex: 1 }}>
              <input
                ref={mobileSearchRef}
                type="text"
                value={mobileSearchQuery}
                onChange={(e) => {
                  setMobileSearchQuery(e.target.value);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') mobileSearchRef.current?.blur(); }}
                placeholder={mobileNavTab === 'Manufacturers' ? 'Search manufacturers & products…' : 'Search products…'}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 14,
                  color: '#333',
                  padding: '4px 0',
                }}
              />
            </div>
            {mobileSearchQuery && (
              <button
                type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', color: '#999', fontSize: 18 }}
                onClick={() => { setMobileSearchQuery(''); }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
            <button
              type="button"
              onClick={handleMobileMicClick}
              aria-label="Voice search"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <svg
                className="m-ali-search-mic"
                width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                style={{ opacity: mobileListening ? 1 : 0.5, color: mobileListening ? '#FF6B00' : 'inherit' }}
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke={mobileListening ? '#FF6B00' : '#666'} strokeWidth="1.5" fill={mobileListening ? 'rgba(255,107,0,0.15)' : 'none'}/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke={mobileListening ? '#FF6B00' : '#666'} strokeWidth="1.5" fill="none"/>
                <line x1="12" y1="19" x2="12" y2="23" stroke={mobileListening ? '#FF6B00' : '#666'} strokeWidth="1.5"/>
                <line x1="8" y1="23" x2="16" y2="23" stroke={mobileListening ? '#FF6B00' : '#666'} strokeWidth="1.5"/>
              </svg>
            </button>
            <button className="m-ali-search-btn" onClick={() => mobileSearchRef.current?.focus()}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="7" stroke="#fff" strokeWidth="2.5" fill="none"/>
                <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#fff" strokeWidth="2.5"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Quick Actions - 3 Cards - Products Tab Only */}
        {mobileNavTab !== 'Manufacturers' && (<>
        <div className="m-ali-quick">
          <div className="m-ali-qa-card" onClick={() => navigate('/all-categories')}>
            <div className="m-ali-qa-icon" style={{background: '#FFF3E6'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="8" height="8" rx="1.5" fill="#FF6B00"/>
                <rect x="13" y="3" width="8" height="8" rx="1.5" fill="#FFB366"/>
                <rect x="3" y="13" width="8" height="8" rx="1.5" fill="#FFB366"/>
                <rect x="13" y="13" width="8" height="8" rx="1.5" fill="#FF6B00"/>
              </svg>
            </div>
            <span className="m-ali-qa-label">Source by<br/>category</span>
          </div>
          <div className="m-ali-qa-card" onClick={openRequirementModalWithAuth}>
            <div className="m-ali-qa-icon" style={{background: '#FFF3E6'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#FF6B00" strokeWidth="2" fill="none"/>
                <path d="M12 8v8M8 12h8" stroke="#FF6B00" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="m-ali-qa-label">Request for<br/>Quotation</span>
          </div>
          <div className="m-ali-qa-card" onClick={() => navigate('/featured-products')}>
            <div className="m-ali-qa-icon" style={{background: '#F5F5F5'}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#FF6B00"/>
              </svg>
            </div>
            <span className="m-ali-qa-label">Top<br/>Ranking</span>
          </div>
        </div>

        {/* Mobile Offer Banner */}
        <div className="m-ali-offers">
          <div className="m-ali-offer-item">
            <span className="m-ali-offer-title">FREE shipping</span>
            <span className="m-ali-offer-sub">on your first order</span>
          </div>
          <div className="m-ali-offer-sep"></div>
          <div className="m-ali-offer-item">
            <span className="m-ali-offer-title">Money-back protection</span>
            <span className="m-ali-offer-sub">for up to 60 days</span>
          </div>
        </div>

        {/* Mobile Category Tabs */}
        <div className="m-ali-cats">
          {['All', 'Metals & Alloys', 'Packaging & Printing', 'Consumer Elec...'].map(cat => (
            <button
              key={cat}
              className={`m-ali-cat ${mobileCatTab === cat ? 'active' : ''}`}
              onClick={() => setMobileCatTab(cat)}
            >
              {cat}
            </button>
          ))}
          <button className="m-ali-cat-more">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>

        {/* Mobile Top Deals Section */}
        <div className="m-ali-section">
          <div className="m-ali-section-hdr">
            <div>
              <h2 className="m-ali-section-title">Top Deals</h2>
              <p className="m-ali-section-sub">Score the lowest prices on Alibaba.com</p>
            </div>
            <Link to="/featured-products" className="m-ali-section-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
          <div className="m-ali-scroll">
            {loading ? (
              <div className="m-ali-loading">Loading deals...</div>
            ) : mobileFilteredFeatured.length > 0 ? mobileFilteredFeatured.map(product => (
              <div className="m-ali-deal-card" key={`deal-${product.id}`} onClick={() => navigate(`/mobile/product/${product.id}`)}>
                <div className="m-ali-deal-img">
                  <img src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} alt={product.name} />
                </div>
                <span className="m-ali-deal-price">{formatPriceRange(product.priceMin, product.priceMax)}</span>
                <span className="m-ali-deal-sub">Lowest among similar</span>
              </div>
            )) : (
              <div className="m-ali-loading">{mobileSearchQuery.trim() ? 'No deals match your search.' : 'No deals available'}</div>
            )}
          </div>
        </div>

        {/* Mobile Tailored Selections Section */}}
        <div className="m-ali-section">
          <div className="m-ali-section-hdr">
            <h2 className="m-ali-section-title">Tailored Selections</h2>
            <Link to="/products" className="m-ali-section-arrow">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
                <polyline points="12 5 19 12 12 19"/>
              </svg>
            </Link>
          </div>
          <div className="m-ali-scroll">
            {loading ? (
              <div className="m-ali-loading">Loading selections...</div>
            ) : mobileFilteredNew.length > 0 ? mobileFilteredNew.map((product, idx) => (
              <div className="m-ali-tail-card" key={`tail-${product.id}`} onClick={() => navigate(`/mobile/product/${product.id}`)}>
                <div className="m-ali-tail-img">
                  <img src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} alt={product.name} />
                  <span className="m-ali-tail-price">{formatPriceRange(product.priceMin, product.priceMax)}</span>
                </div>
                <span className="m-ali-tail-name">{product.name}</span>
                <span className="m-ali-tail-views">{[7, 14, 50, 3, 22][idx % 5]}K+ {idx % 2 === 0 ? 'views' : 'sold'}</span>
              </div>
            )) : (
              <div className="m-ali-loading">{mobileSearchQuery.trim() ? 'No selections match your search.' : 'No selections available'}</div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Tabs */}
        <div className="m-ali-btabs">
          <button className="m-ali-btab active">Picks for you</button>
          <button className="m-ali-btab">Inspirations</button>
        </div>

        {/* Mobile Product Grid Feed - 2 Column Masonry */}
        <div className="m-ali-feed">
          <div className="m-ali-feed-grid">
            {loading ? (
              <div className="m-ali-loading" style={{gridColumn: 'span 2'}}>Loading products...</div>
            ) : (() => {
              const feedItems = [...mobileFilteredFeatured, ...mobileFilteredNew, ...mobileFilteredPopular]
                .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);
              if (feedItems.length === 0) {
                return (
                  <div className="m-ali-loading" style={{gridColumn: 'span 2'}}>
                    {mobileSearchQuery.trim() ? 'No results found.' : 'No products available'}
                  </div>
                );
              }
              return feedItems.map((product, idx) => {
                const seller = product.seller || {};
                const badges = ['Lower priced than similar', 'Lowest among similar', '', 'Fast delivery', ''];
                const badge = badges[idx % badges.length];
                return (
                  <div
                    className="m-ali-feed-card"
                    key={`feed-${product.id}`}
                    onClick={() => navigate(`/mobile/product/${product.id}`)}
                  >
                    <div className="m-ali-feed-img">
                      <img
                        src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'}
                        alt={product.name}
                      />
                    </div>
                    <div className="m-ali-feed-info">
                      <p className="m-ali-feed-name">{product.name || 'Unknown Product'}</p>
                      <p className="m-ali-feed-price">{formatPriceRange(product.priceMin, product.priceMax)}</p>
                      <div className="m-ali-feed-meta">
                        {product.moq && <span>MOQ: {product.moq}</span>}
                        <span>{seller.memberYears || 1} yrs · {(seller.country || 'UAE').substring(0, 2).toUpperCase()}</span>
                      </div>
                      <span className="m-ali-feed-sold">{[18, 42, 120, 7, 55, 200, 34, 91][idx % 8]} sold</span>
                      {badge && (
                        <span className="m-ali-feed-badge">
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M6 1L7.5 4.5L11 5L8.5 7.5L9 11L6 9.5L3 11L3.5 7.5L1 5L4.5 4.5L6 1Z" fill="#FF6B00"/></svg>
                          {badge}
                        </span>
                      )}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
        </>)}

        {/* ===== MANUFACTURERS TAB CONTENT - Mobile Only ===== */}
        {mobileNavTab === 'Manufacturers' && (
          <div className="m-mfg-content">
            {/* Category Tabs Row */}
            <div className="m-mfg-cats-row">
              <div className="m-mfg-cats-scroll">
                <button
                  className={`m-mfg-cat-tab ${mfgSelectedCat === 'All' ? 'active' : ''}`}
                  onClick={() => setMfgSelectedCat('All')}
                >
                  All
                </button>
                {mfgCategories.slice(0, 4).map(cat => (
                  <button
                    key={cat.id}
                    className={`m-mfg-cat-tab ${mfgSelectedCat === cat.name ? 'active' : ''}`}
                    onClick={() => setMfgSelectedCat(cat.name)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <button className="m-mfg-cat-dropdown-btn" onClick={() => setMfgCatModalOpen(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            </div>

            {/* Quick Action Cards */}
            <div className="m-mfg-quick-actions">
              <div className="m-mfg-qa-card" onClick={() => navigate('/all-categories')}>
                <div className="m-mfg-qa-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="8" height="8" rx="1.5" fill="#FF6B00"/>
                    <rect x="13" y="3" width="8" height="8" rx="1.5" fill="#FFB366"/>
                    <rect x="3" y="13" width="8" height="8" rx="1.5" fill="#FFB366"/>
                    <rect x="13" y="13" width="8" height="8" rx="1.5" fill="#FF6B00"/>
                  </svg>
                </div>
                <span className="m-mfg-qa-text">Source by<br/>category</span>
              </div>
              <div className="m-mfg-qa-card" onClick={openRequirementModalWithAuth}>
                <div className="m-mfg-qa-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#FF6B00" strokeWidth="2" fill="none"/>
                    <path d="M12 8v8M8 12h8" stroke="#FF6B00" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <span className="m-mfg-qa-text">Request for<br/>Quotation</span>
              </div>
              <div className="m-mfg-qa-card" onClick={() => navigate('/top-seller')}>
                <div className="m-mfg-qa-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="#FF6B00" strokeWidth="2" fill="none" strokeLinecap="round"/>
                    <polyline points="22 4 12 14.01 9 11.01" stroke="#FF6B00" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="m-mfg-qa-text">Verified Pro<br/>Supplier</span>
              </div>
            </div>

            {mfgLoading ? (
              <div className="m-mfg-loading">Loading manufacturers...</div>
            ) : (
              <>
                {/* Get Samples Section */}
                <div className="m-mfg-section">
                  <div className="m-mfg-section-hdr">
                    <div>
                      <h3 className="m-mfg-section-title">Get samples</h3>
                    </div>
                    <Link to="/products" className="m-mfg-section-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </Link>
                  </div>
                  <div className="m-mfg-hscroll">
                    {mobileFilteredMfgProducts.slice(0, 8).map(product => (
                      <div className="m-mfg-sample-card" key={`sample-${product.id}`} onClick={() => navigate(`/mobile/product/${product.id}`)}>
                        <div className="m-mfg-sample-img">
                          <img src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} alt={product.name} />
                        </div>
                        <span className="m-mfg-sample-label">Sample price:</span>
                        <span className="m-mfg-sample-price">{formatPriceRange(product.priceMin, product.priceMax)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fast Customization Section */}
                <div className="m-mfg-section">
                  <div className="m-mfg-section-hdr">
                    <div>
                      <h3 className="m-mfg-section-title">Fast customization</h3>
                      <p className="m-mfg-section-sub">Low MOQ · 14-day dispatch · True to design</p>
                    </div>
                    <Link to="/products" className="m-mfg-section-arrow">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </Link>
                  </div>
                  <div className="m-mfg-hscroll">
                    {mobileFilteredMfgProducts.slice(3, 11).map((product, idx) => {
                      const tags = ['Main stone', 'logo/pattern', 'Logo/graphic design', 'Custom color', 'Custom size'];
                      return (
                        <div className="m-mfg-custom-card" key={`custom-${product.id}`} onClick={() => navigate(`/mobile/product/${product.id}`)}>
                          <div className="m-mfg-custom-img">
                            <img src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} alt={product.name} />
                            <span className="m-mfg-custom-tag">{tags[idx % tags.length]}</span>
                          </div>
                          <span className="m-mfg-custom-price">{formatPriceRange(product.priceMin, product.priceMax)}</span>
                          <span className="m-mfg-custom-moq">Min. order: {product.moq || '1 Unit'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top-ranking Manufacturers Section */}
                <div className="m-mfg-section">
                  <div className="m-mfg-section-hdr">
                    <h3 className="m-mfg-section-title">Top-ranking manufacturers</h3>
                  </div>
                  <div className="m-mfg-hscroll">
                    {mobileFilteredMfgSellers.slice(0, 6).map(seller => (
                      <div className="m-mfg-rank-card" key={`rank-${seller.id}`} onClick={() => navigate(`/seller/${seller.id}`)}>
                        <div className="m-mfg-rank-img">
                          <img src={seller.coverImage || seller.logo || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600'} alt={seller.name} />
                        </div>
                        <span className="m-mfg-rank-name">{seller.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Manufacturer List - Grouped by Seller */}
                {mobileFilteredMfgGrouped.map(({ seller, products: sellerProducts }) => {
                  const memberYears = seller.memberYears || 1;
                  const staffLabel = seller.numberOfEmployees ? `${seller.numberOfEmployees}+ staff` : '10+ staff';
                  const annualSales = seller.annualSales ? `₹${(seller.annualSales / 1000000).toFixed(1)}M+` : '';
                  return (
                    <div className="m-mfg-manufacturer-block" key={`mfg-${seller.id}`}>
                      <div className="m-mfg-mfr-header" onClick={() => navigate(`/seller/${seller.id}`)}>
                        <div className="m-mfg-mfr-logo">
                          {seller.logo ? (
                            <img src={seller.logo.startsWith('http') ? seller.logo : `${API_BASE}${seller.logo}`} alt={seller.name} />
                          ) : (
                            <div className="m-mfg-mfr-logo-placeholder">
                              {(seller.name || 'U').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="m-mfg-mfr-info">
                          <h4 className="m-mfg-mfr-name">{seller.name}</h4>
                          <p className="m-mfg-mfr-meta">
                            {memberYears} yrs · {staffLabel}
                            {annualSales ? ` · ${annualSales}` : ''}
                          </p>
                          <p className="m-mfg-mfr-desc">
                            {seller.businessType || 'Manufacturer'}
                            {seller.isVerified ? ' · Verified' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="m-mfg-mfr-products">
                        {sellerProducts.slice(0, 3).map(product => (
                          <div className="m-mfg-mfr-prod" key={`mfr-prod-${product.id}`} onClick={() => navigate(`/mobile/product/${product.id}`)}>
                            <div className="m-mfg-mfr-prod-img">
                              <img src={product.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=500'} alt={product.name} />
                              <button className="m-mfg-mfr-prod-search" onClick={(e) => { e.stopPropagation(); navigate(`/products?search=${encodeURIComponent(product.name)}`); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <circle cx="11" cy="11" r="7" stroke="#666" strokeWidth="2" fill="none"/>
                                  <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#666" strokeWidth="2"/>
                                </svg>
                              </button>
                            </div>
                            <span className="m-mfg-mfr-prod-price">{formatPriceRange(product.priceMin, product.priceMax)}</span>
                            <span className="m-mfg-mfr-prod-moq">{product.moq || '1 Unit'} (MOQ)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* Category Modal - Full Screen */}
        {mfgCatModalOpen && (
          <div className="m-mfg-cat-modal-overlay" onClick={() => setMfgCatModalOpen(false)}>
            <div className="m-mfg-cat-modal" onClick={(e) => e.stopPropagation()}>
              <div className="m-mfg-cat-modal-header">
                <h3 className="m-mfg-cat-modal-title">All categories</h3>
                <button className="m-mfg-cat-modal-close" onClick={() => setMfgCatModalOpen(false)}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
              <div className="m-mfg-cat-modal-list">
                <button
                  className={`m-mfg-cat-modal-item ${mfgSelectedCat === 'All' ? 'selected' : ''}`}
                  onClick={() => { setMfgSelectedCat('All'); setMfgCatModalOpen(false); }}
                >
                  <span>All</span>
                  {mfgSelectedCat === 'All' && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
                {mfgCategories.map(cat => (
                  <button
                    key={cat.id}
                    className={`m-mfg-cat-modal-item ${mfgSelectedCat === cat.name ? 'selected' : ''}`}
                    onClick={() => { setMfgSelectedCat(cat.name); setMfgCatModalOpen(false); }}
                  >
                    <span>{cat.name}</span>
                    {mfgSelectedCat === cat.name && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <polyline points="20 6 9 17 4 12" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* Mobile Bottom Navigation Bar */}
      <MobileBottomNav />
      </div>

      {/* Modals */}
      <RequirementModal 
        isOpen={showRequirementModal} 
        onClose={() => setShowRequirementModal(false)} 
      />
      <BecomeSupplierModal 
        isOpen={showBecomeSupplierModal} 
        onClose={() => setShowBecomeSupplierModal(false)}
        onLoginClick={() => {
          setShowBecomeSupplierModal(false);
          setShowLoginModal(true);
        }}
        onVerifyEmail={(email) => {
          setShowBecomeSupplierModal(false);
          // User is registered and logged in, redirect to documents submit
          navigate('/documents-submit');
        }}
      />
      <FindBuyerModal 
        isOpen={showFindBuyerModal} 
        onClose={() => setShowFindBuyerModal(false)}
        onSave={(data) => {
          console.log('Find Buyer data:', data);
          setShowFindBuyerModal(false);
          // Navigate to buyer directory with the tagline as search
          if (data.tagline) {
            navigate(`/buyer-directory?search=${encodeURIComponent(data.tagline)}`);
          } else {
            navigate('/buyer-directory');
          }
        }}
      />
      <ProductDetailsModal 
        isOpen={showProductDetailsModal} 
        onClose={() => {
          setShowProductDetailsModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct ? {
          id: selectedProduct.id,
          image: selectedProduct.image,
          description: selectedProduct.description || selectedProduct.name,
          priceMin: selectedProduct.priceMin,
          priceMax: selectedProduct.priceMax,
          priceUnit: selectedCurrency.code,
          pricePer: selectedProduct.unit || 'Unit',
          moq: selectedProduct.moq || '1 Unit',
          companyName: selectedProduct.seller?.name || 'Unknown Seller',
          sellerId: selectedProduct.seller?.userId || selectedProduct.userId,
          sellerAvatar: selectedProduct.seller?.logo,
          country: selectedProduct.seller?.country || 'UAE',
          countryFlag: selectedProduct.seller?.countryFlag,
          memberYears: selectedProduct.seller?.memberYears || 1,
          businessType: selectedProduct.seller?.businessType || 'Manufacturer',
          isVerified: selectedProduct.seller?.isVerified,
          isGlobal: selectedProduct.seller?.isGlobal,
        } : null}
      />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          setLoginRedirectAction(null);
          setLoginRedirectPath(null);
        }}
        onRegisterClick={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
        onLoginSuccess={() => {
          setShowLoginModal(false);
          if (loginRedirectAction === 'requirement') {
            setLoginRedirectAction(null);
            setShowRequirementModal(true);
          } else if (loginRedirectPath) {
            const path = loginRedirectPath;
            setLoginRedirectPath(null);
            navigate(path);
          }
        }}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onLoginClick={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
      
      {/* Coming Soon Modal */}
      {showComingSoonModal && (
        <div className="coming-soon-modal-overlay" onClick={() => setShowComingSoonModal(false)}>
          <div className="coming-soon-modal" onClick={(e) => e.stopPropagation()}>
            <button className="coming-soon-close" onClick={() => setShowComingSoonModal(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            <div className="coming-soon-icon">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="32" cy="32" r="28" stroke="#1F396C" strokeWidth="3"/>
                <path d="M32 18V34L42 40" stroke="#1F396C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="coming-soon-title">Coming Soon!</h3>
            <p className="coming-soon-text">Buyer Directory feature is under development. Stay tuned for updates!</p>
            <button className="coming-soon-btn" onClick={() => setShowComingSoonModal(false)}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
